import { Alert, Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Breadcrumbs from 'components/breadcrumbs';
import Checkbox from 'components/checkbox';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Pagination from 'components/pagination';
import Paper from 'components/paper';

import Summary from 'components/summary';
import TabPanel from 'components/tab-panel';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HC_patternFill from 'highcharts-pattern-fill';
import HighchartsReact from 'highcharts-react-official';
import { Search } from 'lucide-react';
import Mui from 'materials';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { generatePath, useHistory, useRouteMatch } from 'react-router';
import { FeedData } from 'views/planning-prognosis/types/feed-graph.type';
import { GetFeedGraphInput } from 'views/planning-prognosis/types/get-feed-graph-input.type';
import { PlanningPrognosisSite } from 'views/planning-prognosis/types/planning-prognosis-site.type';

import { useGet } from 'hooks/use-get';
import { usePagination } from 'hooks/use-pagination';
import _, { isEmpty } from 'underscore';
import getLineGraphOptions from 'utils/get-line-graph-options';
import { getCompanyCalculationStatus } from './api/get-company-calculation-status';
import { getPlanAndPrognosisSites } from './api/get-sites';
import { getFeedGraph } from './api/get-total-feed';
import PlanningPrognosisTabsHeader from './components/planning-prognosis-tabs-header';
import SitesSubTabs from './features/sites/components/sites-sub-tabs';

const ActionGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(3),

  '& > *': {
    marginLeft: theme.spacing(2),
  },
}));

enum Tabs {
  ACTIVE = 'active',
  HARVESTED = 'harvested',
  OTHERS = 'others',
}

type ParamTypes = {
  siteTab: Tabs;
};

type Props = {};

HC_patternFill(Highcharts);

const ListSites: React.FC<Props> = () => {
  const intl = useIntl();
  const history = useHistory();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { params, path } = useRouteMatch<ParamTypes>();
  const { account } = useAccountState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGE_NAME_ID = 'planningPrognosis.listSites';
  const PAGINATION_LIMIT = 10;

  const listOptions: HeadCell[] = [
    { id: 'checkbox', label: '', autoWidth: true },
    { id: 'siteName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }) },
    { id: 'activeFishGroups', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.activeFishGroupsLabel` }), align: 'center' },
    { id: 'totalBiomass', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.biomassLabel` }), align: 'right' },
    { id: 'NoFishLabel', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.noFishLabel` }), align: 'right' },
    { id: 'harvestMeanWeight', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.avgWeightLabel` }), align: 'right' },
    { id: 'recalculating', label: '', align: 'center', autoWidth: true, hide: isMobile },
    { id: 'actions', label: '', align: 'center', autoWidth: true },
  ];

  // #region STATE
  const [searchValue, setSearchValue] = useState('');
  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const previousRecalculating = useRef<boolean | undefined>();
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  // #endregion STATE

  // #region FETCH DATA

  const { data: sites, isLoading } = useGet(
    ['getPlanAndPrognosisSites', account.company?.companyId],
    (context) => getPlanAndPrognosisSites(String(context.queryKey[1])),
    {
      enabled: !!account.company?.companyId,
    }
  );

  const { data: totalFeed, isLoading: isLoadingTotalFeed } = useGet(
    [
      'getFeedGraph',
      account.company?.companyId,
      {
        sites: _.compact(
          _.uniq(selectedSites.map((x) => sites?.find((s) => s.siteId === x && s.isUserAuthorized === true)?.globalSiteId))
        ),
      },
    ],
    ({ queryKey }) => getFeedGraph(queryKey[1] as string, queryKey[2] as GetFeedGraphInput),
    {
      enabled: selectedSites.length > 0,
    }
  );

  const { data: calculationStatus } = useGet(
    ['getPlanAndPrognosisCompanyCalculationStatus', account.company?.companyId],
    ({ queryKey }) => getCompanyCalculationStatus(String(queryKey[1])),
    {
      enabled: !!account.company?.companyId,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    if (!calculationStatus) return;

    if (previousRecalculating.current === true && !calculationStatus.recalculating) {
      queryClient.invalidateQueries({
        queryKey: ['getPlanAndPrognosisSites'],
      });
    }

    previousRecalculating.current = calculationStatus.recalculating;
    setRefetchInterval(calculationStatus.recalculating ? 3000 : false);
  }, [calculationStatus, queryClient]);

  // #endregion FETCH DATA

  // #region HANDLERS

  const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: Tabs) => {
    handlePagination(1);
    Object.assign(params, { siteTab: newValue });
    history.push(generatePath(path, params));
  };

  const handleSelectedSites = (value: string, checked: boolean) => {
    setSelectedSites(checked ? selectedSites.concat([value]) : selectedSites.filter((s) => s !== value));
  };

  const handleOnClick = (url: string) => {
    history.push(url);
  };

  // #endregion HANDLERs

  // #region RENDERS

  const renderRows = useCallback(() => {
    let rows: PlanningPrognosisSite[] = [];
    switch (params.siteTab) {
      case Tabs.ACTIVE:
        rows = _.chain(sites)
          .filter((s) => s.activeFishGroups !== 0)
          .filter((s) => s.hasEwosProfiles)
          .value();
        break;
      case Tabs.HARVESTED:
        rows = _.chain(sites)
          .filter((s) => s.activeFishGroups === 0)
          .value();
        break;
      case Tabs.OTHERS:
        rows = _.chain(sites)
          .filter((s) => s.activeFishGroups !== 0)
          .filter((s) => !s.hasEwosProfiles)
          .value();
        break;

      default:
        break;
    }

    return _.chain(rows)
      .filter((s) => (isEmpty(searchValue) ? true : s.siteName.toLowerCase().includes(searchValue.toLowerCase())))
      .sortBy((s) => s.siteName.toLowerCase())
      .sortBy((s) => !s.isUserAuthorized || !s.isMapped)
      .value();
  }, [params.siteTab, searchValue, sites]);

  const renderSite = (row: PlanningPrognosisSite, idx: number, tab: Tabs) => {
    return !row.isUserAuthorized ? (
      <ListRow key={idx}>
        <ListCell />
        <ListCell
          sx={{
            padding: 4,
          }}>
          {row.siteName}
        </ListCell>
        <ListCell sx={{ whiteSpace: { xs: 'normal', md: 'nowrap' } }} colSpan={6}>
          <Box whiteSpace={{ xs: 'normal', md: 'nowrap' }}>
            <FormattedMessage id="profilesStrategies.listFeedStrategies.isUserAuthorized" />
          </Box>
        </ListCell>
      </ListRow>
    ) : (
      <ListRow key={idx} onClick={() => handleOnClick(`/planning-prognosis/sites/${tab}/${row.siteId}/fishgroups`)}>
        <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Checkbox
            tabIndex={-1}
            dark
            hiddenLabel
            checked={selectedSites.includes(row.siteId)}
            onChange={(_, checked) => handleSelectedSites(row.siteId, checked)}
          />
        </ListCell>
        <ListCell>{row.siteName}</ListCell>
        <ListCell align="center">{row.activeFishGroups}</ListCell>
        <ListCell align="right">
          <FormattedNumber value={row.totalBiomass} minimumFractionDigits={0} maximumFractionDigits={0} />
        </ListCell>
        <ListCell align="right">
          <FormattedNumber value={row.totalNumber} />
        </ListCell>
        <ListCell align="right">
          <FormattedNumber value={(row.totalBiomass / row.totalNumber) * 1000} minimumFractionDigits={0} maximumFractionDigits={0} />
        </ListCell>
        <ListCell align="center">
          {row.recalculating && <CircularProgress size={18} />}
        </ListCell>
        <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Box display="flex" justifyContent={{ xs: 'space-between', md: 'flex-end' }} alignItems="center" width={'100%'}>
            <Button
              fullWidth={isMobile}
              size={'small'}
              onClick={() => handleOnClick(`/planning-prognosis/sites/${tab}/${row.siteId}/fishgroups`)}>
              <FormattedMessage id="general.edit" values={{ type: '' }} />
            </Button>
          </Box>
        </ListCell>
      </ListRow>
    );
  };

  const renderGraphData = (arr: FeedData[]): FeedData[] => {
    return Object.entries(_.chain(arr).sortBy('date').groupBy('date').value()).map(([key, value]) => ({
      date: key,
      value: value.reduce((s, f) => s + f.value, 0),
    }));
  };

  const renderAvgMeanWeight = (): number => {
    const sites = renderRows().filter((s) => selectedSites.includes(s.siteId));
    const totalNumber: number = sites.reduce((s, f) => s + f.totalNumber, 0);
    const totalBiomass: number = sites.reduce((s, f) => s + f.totalBiomass, 0);

    return (totalBiomass / totalNumber) * 1000;
  };

  // #endregion RENDERS

  // #region HOOKS

  useEffect(() => {
    setSelectedSites(
      renderRows()
        .filter((s) => s.siteId)
        .map((s) => s.siteId)
    );
    return () => {
      setSelectedSites([]);
    };
  }, [renderRows]);

  // #endregion HOOKS

  return (
    <>
      <PlanningPrognosisTabsHeader utils={<Breadcrumbs />} />
      {calculationStatus?.recalculating && (
        <Alert severity="success" icon={<CircularProgress size={18} />}>
          <Mui.AlertTitle>
            <FormattedMessage id="planningPrognosis.alert.title" />
          </Mui.AlertTitle>
          <FormattedMessage id="planningPrognosis.alert.info" />
        </Alert>
      )}
      <Summary
        items={[
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.activeFishGroupsLabel' }),
            header: (
              <FormattedNumber
                value={renderRows()
                  .filter((s) => selectedSites.includes(s.siteId))
                  .reduce((s, f) => s + f.activeFishGroups, 0)}
              />
            ),
          },
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.biomassLabel' }),
            header: (
              <FormattedNumber
                value={renderRows()
                  .filter((s) => selectedSites.includes(s.siteId))
                  .reduce((s, f) => s + f.totalBiomass, 0)}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
            ),
          },
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.noFishLabel' }),
            header: (
              <FormattedNumber
                value={renderRows()
                  .filter((s) => selectedSites.includes(s.siteId))
                  .reduce((s, f) => s + f.totalNumber, 0)}
              />
            ),
          },
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.avgWeightLabel' }),
            header: <FormattedNumber value={renderAvgMeanWeight() || 0} minimumFractionDigits={0} maximumFractionDigits={0} />,
          },
        ]}
      />
      <SitesSubTabs />
      <Paper transparent marginBottom={0}>
        <ActionGroup>
          <Box
            width={'100%'}
            minWidth={250}
            display={'flex'}
            justifyContent={'space-between'}
            flexDirection={{ xs: 'column', md: 'row' }}>
            <Checkbox
              checked={selectedSites.length === renderRows().length}
              onChange={() =>
                selectedSites.length === renderRows().length
                  ? setSelectedSites([])
                  : setSelectedSites(renderRows().map((s) => s.siteId))
              }
              label={intl.formatMessage({ id: 'general.selectAll' })}
            />
            <TextField
              label={intl.formatMessage({ id: `general.search` })}
              value={searchValue}
              endAdornment={<Search size={18} />}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
            />
          </Box>
        </ActionGroup>

        <TabPanel value={params.siteTab} index={Tabs.ACTIVE}>
          <List
            options={listOptions}
            orderBy="name"
            isLoading={isLoading}
            noResult={{
              show: renderRows().length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
            }}>
            {renderRows()
              .slice(from, to)
              .map((row, idx) => renderSite(row, idx, Tabs.ACTIVE))}
          </List>
        </TabPanel>
        <TabPanel value={params.siteTab} index={Tabs.HARVESTED}>
          <List
            options={listOptions}
            orderBy="name"
            isLoading={isLoading}
            noResult={{
              show: renderRows().length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
            }}>
            {renderRows()
              .slice(from, to)
              .map((row, idx) => renderSite(row, idx, Tabs.HARVESTED))}
          </List>
        </TabPanel>
        <TabPanel value={params.siteTab} index={Tabs.OTHERS}>
          <List
            options={listOptions}
            orderBy="name"
            isLoading={isLoading}
            noResult={{
              show: renderRows().length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
            }}>
            {renderRows()
              .slice(from, to)
              .map((row, idx) => renderSite(row, idx, Tabs.OTHERS))}
          </List>
        </TabPanel>
        <Pagination currentPage={currentPage} pages={Math.ceil(renderRows().length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
      <Paper transparent sx={{ mt: 5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          <Typography variant="h3" color={theme.palette.common.white}>
            <FormattedMessage id="planningPrognosis.feedSummary.title" />
          </Typography>
        </Box>
        <Paper sx={{ mt: 6 }}>
          {isLoadingTotalFeed && <LoadingSpinner overlay />}
          <HighchartsReact
            highcharts={Highcharts}
            options={
              {
                ...getLineGraphOptions({
                  xAxisTitle: intl.formatMessage({ id: `planningPrognosis.feedSummary.graphDate` }),
                  yAxisTitle: intl.formatMessage({ id: `planningPrognosis.feedSummary.graphValue` }),
                  xAxisType: 'datetime',
                  formatNumber: intl.formatNumber,
                }),
                series: [
                  {
                    name: intl.formatMessage({ id: `planningPrognosis.feedSummary.actual` }),
                    color: '#017F62',
                    data: renderGraphData(totalFeed?.actual ?? []).map((point) => ({
                      x: dayjs(point.date).utc(true).valueOf(),
                      y: Number(Number(point.value).toFixed(0)),
                    })),
                  },
                  {
                    name: intl.formatMessage({ id: `planningPrognosis.feedSummary.planned` }),
                    color: theme.palette.bluViu.main,
                    data: renderGraphData(totalFeed?.planned ?? []).map((point) => ({
                      x: dayjs(point.date).utc(true).valueOf(),
                      y: Number(Number(point.value).toFixed(0)),
                    })),
                  },
                  {
                    name: intl.formatMessage({ id: `planningPrognosis.feedSummary.delivered` }),
                    dashStyle: 'ShortDot',
                    visible: false,
                    color: '#7D61B8',
                    data: renderGraphData(totalFeed?.invoiced ?? []).map((point) => ({
                      x: dayjs(point.date).utc(true).valueOf(),
                      y: Number(Number(point.value).toFixed(0)),
                    })),
                  },
                ] as Highcharts.SeriesOptionsType[],
              } as Highcharts.Options
            }
          />
        </Paper>
      </Paper>
    </>
  );
};

export default ListSites;
